// OdontoSDR — vocabulário do domínio (PLANO seções 3.2, 4.1, 4.2).
// Os rótulos vivem aqui para que UI, filtros e relatórios falem a mesma língua.

export const PAPEIS = {
  superadmin: {
    id: 'superadmin',
    label: 'Superadmin',
    descricao: 'Dono do SaaS. Administra clínicas e planos; não entra na área da clínica.',
    espacos: ['vendas', 'recepcao', 'admin'],
  },
  gestor: {
    id: 'gestor',
    label: 'Gestor',
    descricao: 'Vê e configura tudo da clínica: os dois espaços, canais e a equipe.',
    espacos: ['vendas', 'recepcao'],
  },
  sdr: {
    // O id continua 'sdr' — é o que as claims, as rules e as Functions esperam.
    // O rótulo fala a língua da clínica: "SDR" é jargão de quem vende software.
    id: 'sdr',
    label: 'Vendas / Marketing',
    descricao: 'Trabalha os leads: funil, conversas de vendas e agendamentos.',
    espacos: ['vendas'],
  },
  recepcao: {
    id: 'recepcao',
    label: 'Recepção',
    descricao: 'Atende pacientes atuais: confirmações de consulta e conversas da recepção.',
    espacos: ['recepcao'],
  },
};

/**
 * Papéis que um gestor pode atribuir na própria clínica, do escopo mais estreito
 * para o mais amplo. Espelha PAPEIS_CLINICA em functions/lib/guardas.js, que é a
 * autoridade — o deploy empacota só functions/, então os dois lados não podem
 * importar um do outro (mesmo motivo do telefone.js).
 *
 * Superadmin fica de fora de propósito: não é papel de clínica, e o backend
 * recusa quem tentar atribuí-lo.
 */
export const PAPEIS_DA_CLINICA = ['recepcao', 'sdr', 'gestor'];

// Etapas padrão do funil. Cada clínica pode customizar em clinicas/{id}.config.funil.
/**
 * ★ VG — O FUNIL NOVO: sete colunas, nesta ordem. As duas de "não" são VIVAS
 * (a SDR continua ligando em "Não agendou"/"Não compareceu"; o negociador
 * continua em "Não fechou") e exigem MOTIVO de uma lista configurável
 * (`leads/motivos.js`). "Perdido" deixou de ser etapa: é motivo que ENCERRA.
 *
 * ⚠ OS IDs QUE O DINHEIRO E AS CONTAS LEEM NÃO MUDARAM: `novo`, `agendado`,
 * `compareceu`, `fechado` — só o rótulo ("Fechou"). Ids novos: `nao_agendou`,
 * `nao_fechou`; `nao_compareceu` já existia sem coluna.
 */
export const ETAPAS_PADRAO = [
  { id: 'novo', label: 'Novo', cor: '#64748b' },
  { id: 'nao_agendou', label: 'Não agendou', cor: '#0ea5e9' },
  { id: 'agendado', label: 'Agendado', cor: '#a855f7' },
  { id: 'nao_compareceu', label: 'Não compareceu', cor: '#dc2626' },
  { id: 'compareceu', label: 'Compareceu', cor: '#f59e0b' },
  { id: 'nao_fechou', label: 'Não fechou', cor: '#f97316' },
  { id: 'fechado', label: 'Fechou', cor: '#16a34a' },
];

/**
 * As etapas que EXISTIRAM. Só para o passado continuar legível — a timeline de
 * um lead diz "Em contato → Agendado" e precisa do rótulo — e para a migração
 * saber de onde vem. NADA novo pode ser gravado com estes ids (teste de fonte
 * exige zero ocorrências fora daqui e do script de migração).
 */
export const ETAPAS_LEGADAS = [
  { id: 'em_contato', label: 'Em contato (antigo)', cor: '#94a3b8' },
  { id: 'qualificado', label: 'Qualificado (antigo)', cor: '#94a3b8' },
  { id: 'em_negociacao', label: 'Em negociação (antigo)', cor: '#94a3b8' },
  { id: 'perdido', label: 'Perdido (antigo)', cor: '#94a3b8' },
];

// Etapas que aparecem no kanban principal, na ordem do fluxo.
/** As sete, na ordem do kanban. */
export const ETAPAS_KANBAN = ETAPAS_PADRAO.map((e) => e.id);

/**
 * ★ VH-1 — os ids de ORIGEM que EXISTIRAM. Decisão do dono: as origens antigas NÃO
 * aparecem em lugar nenhum — nem rótulo, nem "(antigo)". A LEITURA trata estes ids
 * como VAZIO ("—" na ficha, "Sem origem" no dashboard e no filtro) até a SDR
 * escolher; os 213 documentos de produção continuam com `whatsapp_resgate` gravado
 * (nenhuma migração). Só ids: `ehSemOrigem(id)` em `leads/origens.js` é quem lê.
 * Teste de fonte exige zero ocorrências destes ids entre aspas fora daqui.
 */
export const ORIGENS_LEGADAS = ['whatsapp_resgate', 'meta_ctwa', 'google', 'site', 'balcao', 'outra'];


export const TEMPERATURAS = [
  { id: 'fervendo', label: 'Fervendo', cor: '#dc2626' },
  { id: 'quente', label: 'Quente', cor: '#f97316' },
  { id: 'morno', label: 'Morno', cor: '#f59e0b' },
  { id: 'frio', label: 'Frio', cor: '#0ea5e9' },
];

export const PROCEDIMENTOS_PADRAO = [
  'Avaliação',
  'Implante',
  'Protocolo',
  'Ortodontia',
  'Estética / Lentes',
  'Prótese',
  'Clareamento',
  'Outro',
];

// Tipos de evento da timeline unificada (leads/{id}/interacoes).
export const TIPOS_INTERACAO = {
  nota: 'Nota',
  mudanca_etapa: 'Mudança de etapa',
  ligacao: 'Ligação',
  mensagem: 'Mensagem',
  tarefa: 'Tarefa',
  criacao: 'Criação do lead',
  atribuicao: 'Atribuição',
};

export const etapaPorId = (id) =>
  ETAPAS_PADRAO.find((e) => e.id === id) ??
  ETAPAS_LEGADAS.find((e) => e.id === id) ??
  { id, label: id, cor: '#94a3b8' };

// VH: `origemPorId(config, id)` mora em `leads/origens.js` (lista da clínica → legadas → cru → "—").

// ---------------------------------------------------------------------------
// Canais de WhatsApp (números da clínica) — tela de Configurações → Canais.
//
// Espelha `functions/messaging/numeros.js`, que é a autoridade do lado
// servidor. Duplicado aqui pelo mesmo motivo do `telefone.js`: o deploy das
// Functions empacota só a pasta functions/, então os dois lados não podem
// importar um do outro. Se um papel novo nascer lá, acrescente aqui também.
// ---------------------------------------------------------------------------
export const PAPEIS_NUMERO = {
  marketing_entrada: {
    id: 'marketing_entrada',
    // VT: nome do NÚMERO na tela (Configurações → Canais, QR, atendimento automático).
    label: 'API Oficial',
    descricao: 'Número anunciado nos anúncios. Única porta de entrada de leads novos.',
    // Via oficial: conecta por cadastro na Meta, não por QR Code.
    conectaPorQr: false,
    sufixoInstancia: 'entrada',
  },
  marketing_resgate: {
    id: 'marketing_resgate',
    // ★★★ A-8.1 (14/out/2026, decisão do dono): o rótulo era 'Evolution'. A clínica NÃO
    // precisa saber qual provedor carrega a mensagem — e "Evolution" só dizia isso. O trio
    // na tela passa a ser API Oficial · Vendas · Recepção, que é o que o número SIGNIFICA.
    // ⚠ O id, o `sufixoInstancia` e o `provider` do documento continuam como estão: quem
    // mudou foi a palavra na tela, não o contrato.
    label: 'Vendas',
    descricao: 'Reativação de quem não agendou ou não compareceu.',
    conectaPorQr: true,
    sufixoInstancia: 'resgate',
  },
  recepcao: {
    id: 'recepcao',
    label: 'Recepção',
    descricao: 'Pacientes atuais e confirmações de consulta.',
    conectaPorQr: true,
    sufixoInstancia: 'recepcao',
  },
};

export const papelDoNumeroPorId = (id) =>
  PAPEIS_NUMERO[id] ?? { id, label: id ?? 'Canal', descricao: '', conectaPorQr: false };

/**
 * Estado da conexão de um canal Evolution, como o `statusCanal` grava em
 * `evolution.statusConexao`. Canal antigo não tem o campo — daí o fallback.
 */
export const STATUS_CONEXAO = {
  conectado: { id: 'conectado', label: 'Conectado', cor: '#16a34a' },
  aguardando_qr: { id: 'aguardando_qr', label: 'Aguardando leitura do QR', cor: '#f59e0b' },
  desconectado: { id: 'desconectado', label: 'Desconectado', cor: '#64748b' },
};

export const statusConexaoPorId = (id) => STATUS_CONEXAO[id] ?? STATUS_CONEXAO.desconectado;

/**
 * Quais papéis atendem cada espaço.
 *
 * Morava dentro do `DialogoTransferencia`; subiu para cá quando o seletor de
 * responsável da inbox passou a precisar da mesma resposta. Duas cópias
 * divergiriam no dia em que um papel novo aparecesse — e a divergência seria
 * silenciosa: um lado ofereceria a pessoa, o outro não.
 */
export const PAPEIS_POR_ESPACO = {
  vendas: ['sdr', 'gestor'],
  // ★ VU (19/set/2026, permanente): a SDR atende a recepção — aparece como
  // responsável e como destino de transferência. Espelho de `CANAIS_CONVERSA.recepcao.papeis`.
  recepcao: ['recepcao', 'sdr', 'gestor'],
};

/**
 * ★★ WO — OS RESULTADOS DA TENTATIVA DE CONTATO, num lugar só.
 *
 * ⚠ Era um `const` LOCAL do `FichaLead.jsx:43`. O valor escolhido vai para o campo
 * `resultado` da interação `ligacao` e fica gravado para sempre — mudar a lista lá
 * desalinharia o histórico em silêncio, e o painel de produtividade conta por ele.
 *
 * ★ A ORDEM é a dos botões na ficha, e `atendeu` vem primeiro de propósito: é o único
 * resultado que faz o funil andar.
 */
export const RESULTADOS_LIGACAO = ['atendeu', 'não atende', 'caixa postal', 'número errado'];
